import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BookingHeader } from "../../components/BookingHeader";
import { Loading } from "../../components/Loading";
import { usePluginspace } from "../../hooks/usePluginspace";
import { useAppConfig } from "../../hooks/useAppConfig";
import { ConfigType } from "../../contexts/AppConfigContext";
import api from "../../services/api";
import { datetimeToDateBR } from "../../functions/mask";
import { Sparkles, Copy, Share2 } from "lucide-react";
import Swal from "sweetalert2";
import "./index.css";

type EnvironmentType = {
  id: string | number;
  name: string;
};

type BookingConfigType = {
  id: string;
  able: boolean;
  environment?: EnvironmentType[];
  paymentConfirm?: boolean;
  observation?: string | null;
  cancel?: number;
};

type HourType = {
  id: string | number;
  hour: string;
};

type BookingType = {
  id: string;
  name: string;
  phone: string;
  people: number;
  email: string;
  date: string;
  hour?: HourType;
  bookingEnvironmentId?: string | number;
  customerArrived?: number;
  finalized?: string | null;
  accessibilityNeeds?: string | null;
};

const BookingSuccess: React.FC = () => {
  const { pluginspace_id, phone, bookingId } = useParams();
  const navigate = useNavigate();
  const { registerAccess, getToken } = usePluginspace();
  const { getAppConfig } = useAppConfig();

  const [config, setConfig] = React.useState<ConfigType | void>();
  const [bookingConfig, setBookingConfig] =
    React.useState<BookingConfigType | null>(null);
  const [booking, setBooking] = React.useState<BookingType | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [shareFeedback, setShareFeedback] = React.useState<string | null>(null);

  React.useEffect(() => {
    registerAccess("booking/success");
    let active = true;

    const load = async () => {
      if (!phone) {
        setError("Telefone não informado.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const cfg = await getAppConfig();
        if (!active) return;
        setConfig(cfg);

        const token = await getToken();
        const configResponse = await api.get(
          "/api/queueReservation/bookingConfig",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!active) return;

        const configItem = configResponse.data?.data?.[0];
        if (!configItem || !configItem.able) {
          setError("Reservas indisponíveis para este espaço.");
          setBookingConfig(null);
          setBooking(null);
          return;
        }

        setBookingConfig(configItem);

        const bookingResponse = await api.get(
          `/api/queueReservation/booking/${configItem.id}/${phone}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!active) return;

        const data = bookingResponse.data?.data;
        const list: BookingType[] = Array.isArray(data)
          ? data
          : data
            ? [data]
            : [];

        if (!list.length) {
          setError("Não encontramos uma reserva com esse telefone.");
          setBooking(null);
          return;
        }

        if (bookingId) {
          const selected = list.find(
            (item) => String(item.id) === String(bookingId),
          );

          if (!selected) {
            setError("Não encontramos a reserva selecionada.");
            setBooking(null);
            return;
          }

          setBooking(selected);
          setError(null);
          return;
        }

        const latest = list.sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        )[0];

        setBooking(latest);
        setError(null);
      } catch (err) {
        console.error("BookingSuccess: erro ao carregar os dados", err);
        if (active) {
          setError("Não foi possível carregar os detalhes da reserva.");
          setBooking(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [bookingId, getAppConfig, getToken, phone, registerAccess]);

  const environmentName = React.useMemo(() => {
    if (!booking?.bookingEnvironmentId || !bookingConfig?.environment) {
      return "Ambiente não informado";
    }
    const env = bookingConfig.environment.find(
      (item) => String(item.id) === String(booking.bookingEnvironmentId),
    );
    return env?.name ?? "Ambiente não informado";
  }, [booking, bookingConfig]);

  const formattedPhone = React.useMemo(() => {
    const value = booking?.phone ?? phone ?? "";
    const digits = value.replace(/\D/g, "");
    if (digits.length === 11) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }
    return value || "—";
  }, [booking?.phone, phone]);

  const isFutureReservation = booking
    ? new Date(booking.date).getTime() >= Date.now()
    : false;

  const statusLabel = booking?.finalized
    ? booking.finalized === "canceled"
      ? "Reserva cancelada"
      : "Reserva finalizada"
    : booking?.customerArrived === 1
      ? "Chegada confirmada"
      : isFutureReservation
        ? "Aguardando sua chegada"
        : "Reserva registrada";

  const handleNewBooking = () => {
    if (!pluginspace_id) return;
    navigate(`/${pluginspace_id}/booking`);
  };

  const handleCancelBooking = async () => {
    try {
      if (!bookingConfig?.cancel || !booking?.id) return;

      const confirmResult = await Swal.fire({
        title: "Cancelar reserva?",
        text: "Tem certeza que deseja cancelar esta reserva?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, cancelar",
        cancelButtonText: "Não",
        confirmButtonColor: "#dc2626",
      });

      if (!confirmResult.isConfirmed) return;

      Swal.fire({
        title: "Cancelando reserva...",
        text: "Aguarde enquanto processamos sua solicitação.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const token = await getToken();
      const response = await api.put(
        `/api/queueReservation/booking/${booking.id}`,
        {
          bookingConfigId: bookingConfig.id,
          finalized: "canceled",
          client: true,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const message =
        response?.data?.message || "Reserva alterada com sucesso!";

      Swal.close();

      await Swal.fire({
        icon: "success",
        title: "Reserva cancelada",
        text: message,
        confirmButtonText: "Ok",
      });

      if (pluginspace_id) {
        navigate(`/${pluginspace_id}/booking`);
      } else {
        navigate("/");
      }
    } catch (error: any) {
      console.error("BookingSuccess: erro ao cancelar reserva", error);

      Swal.close();

      const apiTitle =
        error?.response?.data?.error || "Erro ao cancelar reserva!";
      const apiMessage =
        error?.response?.data?.message ||
        "Não foi possível cancelar sua reserva.";

      Swal.fire({
        icon: "error",
        title: apiTitle,
        text: apiMessage,
      });
    }
  };

  const handleEditBooking = () => {
    if (!pluginspace_id || !booking?.id) return;
    navigate(`/${pluginspace_id}/booking/update/${booking.id}`);
  };

  const handleViewMenu = () => {
    if (!pluginspace_id) return;
    navigate(`/${pluginspace_id}`);
  };

  const handleShare = async () => {
    if (!booking) return;
    const dateStr =
      `${datetimeToDateBR(booking.date)} ${booking.hour?.hour ?? ""}`.trim();
    const message = `Reserva confirmada!\nNome: ${booking.name}\nData: ${dateStr}\nAmbiente: ${environmentName}\nPessoas: ${booking.people}`;

    try {
      if (navigator?.share) {
        await navigator.share({
          title: "Reserva confirmada",
          text: message,
          url: window?.location?.href,
        });
        setShareFeedback("Link compartilhado ✅");
      } else if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(message);
        setShareFeedback("Detalhes copiados para a área de transferência");
      } else {
        setShareFeedback("Copie manualmente: recurso não suportado");
      }
    } catch (shareError) {
      console.warn("Compartilhamento cancelado ou indisponível", shareError);
      setShareFeedback("Não foi possível compartilhar agora");
    } finally {
      setTimeout(() => setShareFeedback(null), 4500);
    }
  };

  const handleCopyCode = async () => {
    if (!booking?.id || !navigator?.clipboard?.writeText) {
      setShareFeedback("Copie manualmente: recurso indisponível");
      setTimeout(() => setShareFeedback(null), 4500);
      return;
    }

    try {
      await navigator.clipboard.writeText(booking.id);
      setShareFeedback("Código da reserva copiado ✅");
    } catch (copyError) {
      console.warn("Falha ao copiar o código", copyError);
      setShareFeedback("Não foi possível copiar agora");
    } finally {
      setTimeout(() => setShareFeedback(null), 4500);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div
          style={{
            position: "fixed",
            top: "80px",
            left: 0,
            right: 0,
            height: "calc(100vh - 80px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(248, 249, 250, 0.95)",
            zIndex: 9999,
          }}
        >
          <Loading
            type="spinningBubbles"
            color="#6366f1"
            width={64}
            height={64}
          />
        </div>
      );
    }

    if (error || !booking || !bookingConfig) {
      return (
        <div
          style={{
            background: "#fff",
            padding: "32px",
            borderRadius: "24px",
            textAlign: "center",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
          }}
        >
          <h2 style={{ margin: "0 0 12px", fontSize: "24px" }}>Ops! 😕</h2>
          <p style={{ margin: "0 0 24px", color: "#475569" }}>
            {error ?? "Não encontramos os dados da reserva."}
          </p>
          <button
            style={{
              padding: "14px 16px",
              borderRadius: "16px",
              border: "none",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "#fff",
              fontWeight: 700,
              cursor: "pointer",
              width: "100%",
            }}
            onClick={handleNewBooking}
          >
            Fazer nova reserva
          </button>
        </div>
      );
    }

    const showPaymentStep = Boolean(bookingConfig.paymentConfirm);

    return (
      <div style={{ maxWidth: "800px", margin: "0 auto", width: "100%" }}>
        <div
          style={{
            width: "100%",
            maxWidth: "500px",
            margin: "0 auto 40px",
            padding: "0 20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: showPaymentStep
                ? "space-between"
                : "space-around",
              position: "relative",
              padding: "0 40px",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "20px",
                left: showPaymentStep ? "60px" : "25%",
                right: showPaymentStep ? "86px" : "25%",
                height: "2px",
                background: "rgba(0, 0, 0, 0.1)",
                zIndex: 0,
              }}
            ></div>
            <div
              style={{
                position: "absolute",
                top: "20px",
                left: showPaymentStep ? "60px" : "25%",
                right: showPaymentStep ? "86px" : "25%",
                height: "2px",
                background: "linear-gradient(135deg, #4338ca 0%, #764ba2 100%)",
                zIndex: 1,
              }}
            ></div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 2,
                cursor: "pointer",
              }}
              onClick={handleNewBooking}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(135deg, #4338ca 0%, #764ba2 100%)",
                  border: "2px solid transparent",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                }}
              >
                <svg
                  style={{ width: "20px", height: "20px", color: "#fff" }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span
                style={{
                  marginTop: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#4338ca",
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                Dados
              </span>
            </div>

            {showPaymentStep && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  zIndex: 2,
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg, #4338ca 0%, #764ba2 100%)",
                    border: "2px solid transparent",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  <svg
                    style={{ width: "20px", height: "20px", color: "#fff" }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <span
                  style={{
                    marginTop: "8px",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#4338ca",
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  Pagamento
                </span>
              </div>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 3,
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(135deg, #4338ca 0%, #764ba2 100%)",
                  border: "2px solid #fff",
                  boxShadow: "0 4px 20px rgba(102, 126, 234, 0.5)",
                }}
              >
                <svg
                  style={{ width: "20px", height: "20px", color: "#fff" }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <span
                style={{
                  marginTop: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#4338ca",
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                Sucesso
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderRadius: "24px",
            padding: "32px 24px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "32px" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                background:
                  "linear-gradient(135deg, rgba(102, 187, 106, 0.15) 0%, rgba(76, 175, 80, 0.15) 100%)",
                marginBottom: "20px",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 32px rgba(76, 175, 80, 0.4)",
                }}
              >
                <svg
                  style={{ width: "40px", height: "40px", color: "#fff" }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <h1
              style={{
                margin: "0 0 12px 0",
                fontSize: "32px",
                fontWeight: 700,
                color: "#4338ca",
                fontFamily: "'Poppins', sans-serif",
                letterSpacing: "-0.5px",
              }}
            >
              Reserva Confirmada!
            </h1>
            <p
              style={{
                margin: 0,
                color: "#4338ca",
                fontSize: "16px",
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 400,
                lineHeight: 1.6,
              }}
            >
              Sua reserva foi realizada com sucesso. Guarde o código abaixo para
              consultas.
            </p>
            <div
              style={{
                marginTop: "12px",
                display: "inline-flex",
                padding: "6px 18px",
                borderRadius: "999px",
                background: "rgba(16, 185, 129, 0.12)",
                color: "#4338ca",
                fontWeight: 600,
                fontSize: "12px",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {statusLabel}
            </div>
          </div>

          <div
            style={{
              background:
                "linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.08) 100%)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              padding: "32px 24px",
              borderRadius: "24px",
              marginBottom: "24px",
              border: "1px solid rgba(102, 126, 234, 0.25)",
              textAlign: "center",
              boxShadow:
                "0 8px 24px rgba(102, 126, 234, 0.15), 0 4px 12px rgba(102, 126, 234, 0.1)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                fontSize: "22px",
                color: "#4338ca",
                fontWeight: 600,
                marginBottom: "12px",
                textTransform: "uppercase",
                fontFamily: "'Poppins', sans-serif",
                letterSpacing: "1.5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <Sparkles size={16} color="#4338ca" />
              Código da Reserva
            </div>
            <div
              style={{
                background: "rgba(99, 102, 241, 0.1)",
                padding: "16px 20px",
                borderRadius: "16px",
                marginBottom: "16px",
                border: "1px solid rgba(99, 102, 241, 0.2)",
              }}
            >
              <div
                style={{
                  color: "#4338ca",
                  fontSize: "28px",
                  fontWeight: 800,
                  fontFamily: "'Poppins', sans-serif",
                  letterSpacing: "2px",
                  wordBreak: "break-all",
                }}
              >
                {booking.id}
              </div>
            </div>
            <div className="booking-success-code-actions">
              <button
                onClick={handleCopyCode}
                aria-label="Copiar código da reserva"
                title="Copiar código da reserva"
                style={{
                  padding: "12px",
                  borderRadius: "12px",
                  border: "2px solid #6366f1",
                  background: "#fff",
                  color: "#4338ca",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Copy size={20} strokeWidth={2.5} />
              </button>
              <button
                onClick={handleShare}
                aria-label="Compartilhar detalhes da reserva"
                title="Compartilhar detalhes da reserva"
                style={{
                  padding: "12px",
                  borderRadius: "12px",
                  border: "2px solid #8b5cf6",
                  background: "#fff",
                  color: "#8b5cf6",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Share2 size={20} strokeWidth={2.5} />
              </button>
            </div>
            {shareFeedback && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "10px 16px",
                  borderRadius: "12px",
                  background: "rgba(34, 197, 94, 0.12)",
                  color: "#15803d",
                  fontWeight: 600,
                }}
              >
                {shareFeedback}
              </div>
            )}
          </div>

          <div className="booking-success-details">
            <div className="booking-success-details__header">
              <svg
                className="booking-success-details__icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <div>
                <h3>Detalhes da Reserva</h3>
                <p>{statusLabel}</p>
              </div>
            </div>
            <div className="booking-success-details__body">
              {[
                { label: "Data", value: datetimeToDateBR(booking.date) },
                { label: "Horário", value: booking.hour?.hour ?? "—" },
                { label: "Ambiente", value: environmentName },
                {
                  label: "Pessoas",
                  value: `${booking.people} ${booking.people > 1 ? "pessoas" : "pessoa"}`,
                },
                { label: "Nome", value: booking.name },
                { label: "Telefone", value: formattedPhone },
                { label: "E-mail", value: booking.email, wide: true },
              ].map((item) => (
                <div
                  key={item.label}
                  className={`booking-success-details__row${item.wide ? " booking-success-details__row--wide" : ""}`}
                >
                  <p className="booking-success-details__row-label">
                    {item.label}
                  </p>
                  <p className="booking-success-details__row-value">
                    {item.value}
                  </p>
                </div>
              ))}
              {booking.accessibilityNeeds && (
                <div className="booking-success-details__row booking-success-details__row--wide booking-success-details__row--accent">
                  <p className="booking-success-details__row-label">
                    Acessibilidade
                  </p>
                  <p className="booking-success-details__row-value">
                    {booking.accessibilityNeeds}
                  </p>
                </div>
              )}
            </div>
          </div>

          {bookingConfig.observation && (
            <div
              className="observation-queue"
              style={{
                padding: "20px",
                background: "rgba(99, 102, 241, 0.05)",
                borderRadius: "16px",
                border: "1px solid rgba(99, 102, 241, 0.1)",
                marginTop: "24px",
                fontSize: "14px",
                lineHeight: 1.6,
                color: "#2d3436",
                fontFamily: "'Poppins', sans-serif",
              }}
              dangerouslySetInnerHTML={{ __html: bookingConfig.observation }}
            ></div>
          )}

          <div className="booking-success-action-grid">
            <button
              onClick={handleEditBooking}
              style={{
                padding: "14px 16px",
                borderRadius: "16px",
                border: "2px solid #6366f1",
                background: "#fff",
                color: "#4338ca",
                fontSize: "14px",
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Alterar reserva
            </button>
            <button
              onClick={handleNewBooking}
              style={{
                padding: "14px 16px",
                borderRadius: "16px",
                border: "2px solid #8b5cf6",
                background: "#fff",
                color: "#4338ca",
                fontSize: "14px",
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Nova reserva
            </button>
          </div>

          <button
            onClick={handleViewMenu}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 8px 24px rgba(102, 126, 234, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 16px rgba(102, 126, 234, 0.3)";
            }}
            style={{
              width: "100%",
              padding: "16px",
              borderRadius: "16px",
              border: "none",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "#ffffff",
              fontSize: "16px",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              cursor: "pointer",
              marginTop: "16px",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)",
            }}
          >
            Ver cardápio
          </button>

          {Number(bookingConfig.cancel) > 0 && (
            <section className="booking-success-cancel-card">
              <p className="booking-success-cancel-card__title">
                Precisa cancelar?
              </p>
              <p className="booking-success-cancel-card__description">
                Caso surja algum imprevisto, cancele sua reserva com
                antecedência.
              </p>
              <button
                onClick={handleCancelBooking}
                className="booking-success-cancel-button"
              >
                Cancelar reserva
              </button>
            </section>
          )}

          <section
            className="booking-success__tips"
            style={{ marginTop: "16px" }}
          >
            <h3>Dicas rápidas</h3>
            <ul>
              <li>
                Chegue alguns minutos antes para validar seus dados com a
                equipe.
              </li>
              <li>
                Caso precise alterar qualquer informação, use o botão "Alterar
                reserva" ou entre em contato com a loja.
              </li>
              <li>
                Se não puder comparecer, cancele com antecedência para liberar o
                espaço para outros clientes.
              </li>
            </ul>
            {config && (
              <p className="booking-success__brand">
                {config.name ? `${config.name} ` : ""}
              </p>
            )}
          </section>
        </div>
      </div>
    );
  };

  return (
    <>
      <BookingHeader />
      <div
        key={phone || "booking-success"}
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          padding: "40px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            width: "100%",
          }}
        >
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default BookingSuccess;
